import { Injectable } from '@angular/core';
import { ConsolidatedError, ExcelRow, ValidationError } from '../doqfy-components/bulk-upload-pdf-estamp/bulk-upload.interfaces';

@Injectable({ providedIn: 'root' })
export class EstampValidatorService {
  readonly requiredColumns: string[] = [
    'referance_id',
    'branch_code',
    'stamp_type',
    'first_party_name',
    'second_party_name',
    'stamp_duty_paid_by',
    'stamp_duty_amount',
    'description',
    'article_code',
    'consideration_amount',
    'execution_date',
    'file_name',
  ];

  validate(
    rows: ExcelRow[],
    userBranches: string[],
    refIdFileNameValidation: boolean = false
  ): EstampValidationResult {
    const issueList: ValidationError[]          = [];
    const branchErrors: ValidationError[]       = [];
    const refDuplicates: ValidationError[]      = [];
    const fileNameDuplicates: ValidationError[] = [];
    const refIdFileNameErrors: ValidationError[] = [];
    const rowsWithIssuesSet = new Set<ExcelRow>();

    // 1. Branch code check
    rows.forEach((row, index) => {
      if (
        row.branch_code &&
        !userBranches.map(b => b.toLowerCase()).includes(row.branch_code.toLowerCase())
      ) {
        branchErrors.push({
          rowNumber: index + 2,
          error: `Branch Code "${row.branch_code}" does not belong to your account.`,
        });
        rowsWithIssuesSet.add(row);
      }
    });

    // 2. Duplicate reference_id check
    const seenRefIds = new Set<string>();
    rows.forEach((row, index) => {
      if (row.referance_id) {
        const key = row.referance_id.toString().toLowerCase().trim();
        if (seenRefIds.has(key)) {
          refDuplicates.push({
            rowNumber: index + 2,
            error: `Duplicate reference_id found: ${row.referance_id}`,
          });
          rowsWithIssuesSet.add(row);
        } else {
          seenRefIds.add(key);
        }
      }
    });

    // 3. Same file_name must NOT appear under a different referance_id.
    // For eStamp: one row = one referance_id = one file. If the same file_name
    // appears under two different referance_ids that is an error.
    const fileToRefId = new Map<string, string>();
    rows.forEach((row, index) => {
      const fileName = row.file_name?.toString().trim().toLowerCase() ?? '';
      const refId    = row.referance_id?.toString().trim().toLowerCase() ?? '';
      if (!fileName || !refId) return;

      if (fileToRefId.has(fileName)) {
        if (fileToRefId.get(fileName) !== refId) {
          const errMsg = `File "${row.file_name}" is already used under a different reference ID. Each file must belong to only one reference ID.`;
          fileNameDuplicates.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        }
      } else {
        fileToRefId.set(fileName, refId);
      }
    });

    // 4. Field-level validation per row
    rows.forEach((row, index) => {
      const errors: string[] = [];

      const st = this._validateStampType(row.stamp_type);
      if (st) errors.push(st);

      const fp = this._validateFirstPartyName(row.first_party_name, row.stamp_type);
      if (fp) errors.push(fp);

      const sp = this._validateSecondPartyName(row.second_party_name, row.stamp_type);
      if (sp) errors.push(sp);

      const sdpb = this._validateStampDutyPaidBy(row.stamp_duty_paid_by);
      if (sdpb) errors.push(sdpb);

      const sda = this._validateStampDutyAmount(row.stamp_duty_amount);
      if (sda) errors.push(sda);

      const desc = this._validateDescription(row.description);
      if (desc) errors.push(desc);

      const ac = this._validateArticleCode(row.article_code);
      if (ac) errors.push(ac);

      const ca = this._validateConsiderationAmount(row.consideration_amount);
      if (ca) errors.push(ca);

      const ed = this._validateExecutionDate(row.execution_date);
      if (ed) errors.push(ed);

      const fn = this._validateFileName(row.file_name);
      if (fn) errors.push(fn);

      if (errors.length > 0) {
        issueList.push({ rowNumber: index + 2, error: errors.join(' ') });
        rowsWithIssuesSet.add(row);
      }
    });

    // 5. Reference ID must exactly match filename without .pdf extension (case-sensitive)
    if (refIdFileNameValidation) {
      const checkedFiles = new Set<string>();
      rows.forEach((row, index) => {
        const fileName = row.file_name?.toString().trim() ?? '';
        if (!fileName || checkedFiles.has(fileName.toLowerCase())) return;
        checkedFiles.add(fileName.toLowerCase());

        const refId = row.referance_id?.toString().trim() ?? '';
        const fileNameWithoutExt = fileName.replace(/\.pdf$/i, '');

        if (refId !== fileNameWithoutExt) {
          const errMsg = `Reference ID "${refId || '(empty)'}" must exactly match the filename without extension "${fileNameWithoutExt}" (case-sensitive).`;
          refIdFileNameErrors.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        }
      });
    }

    // 6. Consolidate all errors (deduplicated)
    const consolidatedErrors = this._consolidate([
      ...issueList,
      ...branchErrors,
      ...refDuplicates,
      ...fileNameDuplicates,
      ...refIdFileNameErrors,
    ]);

    // 7. Calculate valid row count
    const allErrorRows = new Set<number>([
      ...issueList,
      ...branchErrors,
      ...refDuplicates,
      ...fileNameDuplicates,
      ...refIdFileNameErrors,
    ].map(e => e.rowNumber!));

    const validRowCount = rows.filter((_, i) => !allErrorRows.has(i + 2)).length;

    return {
      isValid: consolidatedErrors.length === 0,
      consolidatedErrors,
      rowsWithIssues: Array.from(rowsWithIssuesSet),
      validRowCount,
      fileNameDuplicateErrors: fileNameDuplicates,
    };
  }

  // ── Consolidation helper ─────────────────────────────────────────────────
  private _consolidate(errors: ValidationError[]): ConsolidatedError[] {
    const seen = new Set<string>();
    const result: ConsolidatedError[] = [];
    errors.forEach(e => {
      const text = `Row ${e.rowNumber} : ${e.error}`;
      if (!seen.has(text)) {
        seen.add(text);
        result.push({ displayText: text, originalItem: e });
      }
    });
    return result;
  }

  // ── Field validators ─────────────────────────────────────────────────────
  private _validateStampType(v: any): string | null {
    if (!v || typeof v !== 'string' || v.trim() === '') return 'Invalid or missing Stamp Type.';
    return null;
  }

  private _validateFirstPartyName(v: any, stampType?: string): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing First Party Name.';
    if (stampType?.toLowerCase() === 'traditional') return null;
    if (v.length > 50) return 'First Party Name should not exceed 50 characters.';
    if (!/^[a-zA-Z0-9\s]+$/.test(v)) return 'First Party Name should not contain special characters.';
    return null;
  }

  private _validateSecondPartyName(v: any, stampType?: string): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing Second Party Name.';
    if (stampType?.toLowerCase() === 'traditional') return null;
    if (v.length > 50) return 'Second Party Name should not exceed 50 characters.';
    if (!/^[a-zA-Z0-9\s.\/&()]+$/.test(v)) return 'Second Party Name should not contain special characters.';
    return null;
  }

  private _validateStampDutyPaidBy(v: any): string | null {
    if (!v || v.length <= 0) return 'Invalid or missing Stamp Duty Paid By.';
    if (v !== 'first_party_name' && v !== 'second_party_name')
      return 'Stamp duty paid by should be either first_party_name or second_party_name.';
    return null;
  }

  private _validateStampDutyAmount(v: any): string | null {
    if (!Number.isFinite(v) || v <= 0) return 'Invalid or missing Stamp Duty Amount.';
    if (typeof v !== 'number') return 'Stamp duty amount should be a number.';
    if (v % 1 !== 0) return 'Stamp duty amount cannot be decimal.';
    return null;
  }

  private _validateDescription(v: any): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing Description.';
    if (v.length > 100) return 'Description should not exceed 100 characters.';
    return null;
  }

  private _validateArticleCode(v: any): string | null {
    if (!v) return 'Invalid or missing Article ID.';
    return null;
  }

  private _validateConsiderationAmount(v: any): string | null {
    if (!Number.isFinite(v) || v < 0) return 'Invalid or missing Consideration Amount.';
    if (typeof v !== 'number') return 'Consideration amount should be a number.';
    return null;
  }

  private _validateExecutionDate(v: any): string | null {
    if (!v) return 'Invalid or missing Execution Date.';

    const s = v.toString().trim();

    // Accept d-m-yyyy or dd-mm-yyyy format
    const ddmmyyyy = /^\d{1,2}-\d{1,2}-\d{4}$/;
    if (!ddmmyyyy.test(s)) {
      return 'Execution Date must be in dd-mm-yyyy format (e.g. 01-02-2026 or 1-2-2026).';
    }

    const [d, m, y] = s.split('-').map(Number);
    const parsedDate = new Date(y, m - 1, d);

    // Check if date is actually valid (e.g. 31-02-2026 should fail)
    if (
      parsedDate.getFullYear() !== y ||
      parsedDate.getMonth() !== m - 1 ||
      parsedDate.getDate() !== d
    ) {
      return 'Execution Date is not a valid calendar date.';
    }

    // Check future date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsedDate > today) {
      return 'Execution Date cannot be a future date.';
    }

    return null;
  }
  private _validateFileName(v: any): string | null {
    if (!v) return 'file_name is required.';
    if (!v.toString().toLowerCase().endsWith('.pdf')) return `file_name "${v}" must be a PDF file.`;
    return null;
  }
}

export interface EstampValidationResult {
  isValid: boolean;
  consolidatedErrors: ConsolidatedError[];
  rowsWithIssues: ExcelRow[];
  validRowCount: number;
  fileNameDuplicateErrors: ValidationError[];
}